
import React from 'react';
import { 
  MessageSquare, 
  FileText, 
  Database, 
  Cpu, 
  ShieldCheck, 
  Zap,
  ArrowRight,
  Workflow,
  Activity,
  Network,
  ChevronRight,
  RefreshCw,
  UserCheck,
  Repeat
} from 'lucide-react';

interface FlipCardProps {
  title: string;
  frontText: string;
  backText: string;
  icon: React.ReactNode;
  color: string;
}

const FlipCard: React.FC<FlipCardProps> = ({ title, frontText, backText, icon, color }) => {
  return (
    <div className="group w-full h-32 [perspective:1000px]">
      <div className="relative w-full h-full transition-all duration-700 [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
        {/* Front Side */}
        <div className="absolute inset-0 w-full h-full bg-white border border-zodiac/10 rounded-2xl shadow-sm flex items-center px-6 [backface-visibility:hidden]">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-6 ${color} bg-opacity-10`}>
            {icon}
          </div>
          <div className="text-left">
            <h4 className="text-sm font-black text-zodiac uppercase tracking-tight mb-1">{title}</h4>
            <p className="text-[11px] text-zodiac/50 font-medium leading-tight">{frontText}</p>
          </div>
          <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-6 h-6 rounded-full bg-brandBg flex items-center justify-center">
               <ChevronRight className="w-3 h-3 text-accent" />
            </div>
          </div>
        </div>

        {/* Back Side */}
        <div className="absolute inset-0 w-full h-full bg-zodiac rounded-2xl p-6 flex flex-col justify-center items-center [transform:rotateY(180deg)] [backface-visibility:hidden] text-white overflow-hidden text-center">
          <div className="absolute top-0 right-0 w-24 h-24 bg-accent/10 blur-2xl rounded-full translate-x-1/2 -translate-y-1/2"></div>
          <p className="text-[12px] leading-relaxed font-light italic relative z-10 w-full">
            {backText}
          </p>
        </div>
      </div>
    </div>
  );
};

export const Architecture: React.FC = () => {
  return (
    <section id="architecture" className="py-40 bg-brandBg px-6 md:px-12 border-y border-zodiac/5 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-40 max-w-4xl mx-auto">
          <p className="text-[10px] font-black uppercase tracking-[0.5em] text-accent mb-4">INTELLIGENCE ARCHITECTURE</p>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter mb-8 text-zodiac leading-[1.05]">The Decision-to-Action Blueprint</h2>
          <p className="text-xl text-zodiac/60 font-light leading-relaxed max-w-3xl mx-auto">
            A non-invasive, bi-directional intelligence overlay that governs how enterprise signals turn into governed execution and continuous learning loops.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-0 items-center relative">
          
          {/* HIGH-FIDELITY FLOW SVG LAYER - DETACHED MOTION */}
          <div className="absolute inset-0 pointer-events-none hidden lg:block z-0">
             <svg viewBox="0 0 1200 600" className="w-full h-full" preserveAspectRatio="none">
                <defs>
                   <filter id="archSoftGlow" x="-20%" y="-20%" width="140%" height="140%">
                      <feGaussianBlur in="SourceGraphic" stdDeviation="4" />
                   </filter>
                </defs>
                
                {/* GLOBAL LEARNING LOOP ARC */}
                <path 
                  id="globalLearningArc"
                  d="M 1000 200 C 1000 0, 200 0, 200 200" 
                  stroke="#00A1E4" 
                  strokeWidth="1.5" 
                  strokeDasharray="8 8"
                  className="opacity-10"
                  fill="none"
                />
                
                {/* Learning Loop Particles */}
                {[0, 1, 2].map((i) => (
                  <circle key={`loop-${i}`} r="3" fill="#00A1E4" filter="url(#archSoftGlow)">
                    <animateMotion dur="6s" begin={`${i * 2}s`} repeatCount="indefinite" path="M 1000 200 C 1000 0, 200 0, 200 200" />
                  </circle>
                ))}

                {/* --- Forward Flow: Left (Integration) to Center (Platform) --- */}
                <path d="M 280 240 Q 340 240, 400 240" stroke="#00A1E4" strokeWidth="1.5" strokeDasharray="4 6" opacity="0.1">
                   <animate attributeName="stroke-dashoffset" from="20" to="0" dur="1s" repeatCount="indefinite" />
                </path>
                <circle r="2.5" fill="#00A1E4" opacity="0.6" filter="url(#archSoftGlow)">
                   <animateMotion dur="2s" repeatCount="indefinite" path="M 280 240 Q 340 240, 400 240" />
                </circle>
                
                {/* --- Return Flow: Center (Platform) to Left (Integration) - RIGHT TO LEFT DIRECTION --- */}
                <path d="M 400 360 Q 340 360, 280 360" stroke="#00A1E4" strokeWidth="1.5" strokeDasharray="4 6" opacity="0.1">
                   <animate attributeName="stroke-dashoffset" from="20" to="0" dur="1.5s" repeatCount="indefinite" />
                </path>
                <circle r="2.5" fill="#00A1E4" opacity="0.4" filter="url(#archSoftGlow)">
                   <animateMotion dur="2.5s" repeatCount="indefinite" path="M 400 360 Q 340 360, 280 360" />
                </circle>

                {/* --- Forward Flow: Center (Platform) to Right (Execution) --- */}
                <path d="M 800 240 Q 860 240, 920 240" stroke="#00A1E4" strokeWidth="1.5" strokeDasharray="4 6" opacity="0.1">
                   <animate attributeName="stroke-dashoffset" from="20" to="0" dur="1.2s" repeatCount="indefinite" />
                </path>
                <circle r="2.5" fill="#00A1E4" opacity="0.6" filter="url(#archSoftGlow)">
                   <animateMotion dur="2s" repeatCount="indefinite" path="M 800 240 Q 860 240, 920 240" />
                </circle>

                {/* --- Return Flow: Right (Execution) to Center (Platform) - RIGHT TO LEFT DIRECTION --- */}
                <path d="M 920 360 Q 860 360, 800 360" stroke="#00A1E4" strokeWidth="1.5" strokeDasharray="4 6" opacity="0.1">
                   <animate attributeName="stroke-dashoffset" from="20" to="0" dur="1.8s" repeatCount="indefinite" />
                </path>
                <circle r="2.5" fill="#00A1E4" opacity="0.4" filter="url(#archSoftGlow)">
                   <animateMotion dur="3s" repeatCount="indefinite" path="M 920 360 Q 860 360, 800 360" />
                </circle>
             </svg>
          </div>

          {/* COLUMN 1: INTEGRATION LAYER */}
          <div className="lg:col-span-3 relative z-10 space-y-4 px-4">
            <div className="mb-14 text-center">
               <div className="flex items-center justify-center gap-3 mb-3">
                 <Repeat className="w-4 h-4 text-accent" />
                 <h3 className="text-sm font-black text-zodiac uppercase tracking-[0.3em]">Integration Layer</h3>
               </div>
               <div className="w-12 h-1 bg-accent mx-auto"></div>
            </div>

            <div className="space-y-4">
              {[
                { name: "Unstructured Signals", i: <MessageSquare className="w-4 h-4" />, sub: "Email, Chat, Voice" },
                { name: "Documents & Data", i: <FileText className="w-4 h-4" />, sub: "PDF, CSV, Excel" },
                { name: "ERP & SaaS", i: <Database className="w-4 h-4" />, sub: "SAP, Oracle, D365" },
                { name: "IoT & Operational Systems", i: <Cpu className="w-4 h-4" />, sub: "PLC, Edge Devices" }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center space-x-5 p-6 bg-white border border-zodiac/5 rounded-[1.8rem] shadow-sm hover:shadow-xl hover:border-accent/20 transition-all duration-500 group/item">
                  <div className="w-10 h-10 rounded-xl bg-brandBg flex items-center justify-center text-zodiac/40 group-hover/item:text-accent transition-colors shadow-inner">
                    {item.i}
                  </div>
                  <div className="text-left">
                    <p className="text-[11px] font-black uppercase tracking-tight text-zodiac">{item.name}</p>
                    <p className="text-[9px] font-bold text-zodiac/30 uppercase tracking-widest">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-1"></div>

          {/* COLUMN 2: GROFLEX PLATFORM (CENTRAL STAGE) */}
          <div className="lg:col-span-4 relative z-10">
            <div className="bg-white border border-zodiac/10 rounded-[4rem] p-1.5 shadow-[0_50px_120px_-30px_rgba(17,34,72,0.12)] overflow-hidden group">
              <div className="p-12 bg-brandBg/50 border-b border-zodiac/5 flex flex-col items-center text-center">
                <h3 className="text-lg font-black text-zodiac uppercase tracking-[0.25em] mb-4">Groflex Platform</h3>
                <div className="px-6 py-2.5 bg-accent/10 border border-accent/20 rounded-full flex items-center gap-3">
                   <RefreshCw className="w-4 h-4 text-accent animate-spin-slow" />
                   <span className="text-[10px] font-black uppercase tracking-widest text-accent">Active Operational Engine</span>
                </div>
              </div>

              <div className="p-10 pb-12 space-y-5">
                <FlipCard 
                  title="Data Intelligence Layer"
                  frontText="Structures signals into a unified operational timeline."
                  backText="Universal Data Connector normalizes multi-cloud and legacy inputs into a high-resolution context."
                  icon={<Network className="w-6 h-6" />}
                  color="text-blue-500"
                />
                <FlipCard 
                  title="AI Intelligence Layer"
                  frontText="Applies agentic reasoning to access execution risk."
                  backText="Proprietary Agent Orchestrator runs parallel simulations to detect drift across siloed systems."
                  icon={<Workflow className="w-6 h-6" />}
                  color="text-indigo-600"
                />
                <FlipCard 
                  title="Decision Layer"
                  frontText="Prioritizes emerging risks and determines impact."
                  backText="Cognitive scoring weights operational signals against financial KPIs to surface only high-impact actions."
                  icon={<Activity className="w-6 h-6" />}
                  color="text-accent"
                />
              </div>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-1"></div>

          {/* COLUMN 3: EXECUTION LAYER */}
          <div className="lg:col-span-3 relative z-10 space-y-4 px-4">
            <div className="mb-14 text-center">
               <div className="flex items-center justify-center gap-3 mb-3">
                 <Zap className="w-4 h-4 text-accent" />
                 <h3 className="text-sm font-black text-zodiac uppercase tracking-[0.3em]">Execution Layer</h3>
               </div>
               <div className="w-12 h-1 bg-accent mx-auto"></div>
            </div>

            <div className="space-y-4">
              {[
                { name: "Financial & Operational Execution", i: <Database className="w-4 h-4" />, sub: "ERP — SAP, Oracle" },
                { name: "Commercial Execution", i: <UserCheck className="w-4 h-4" />, sub: "CRM — Salesforce, HubSpot" },
                { name: "Physical Supply Chain Execution", i: <Workflow className="w-4 h-4" />, sub: "WMS / MES / TMS — Siemens, Infor" },
                { name: "Human Execution & Accountability", i: <ShieldCheck className="w-4 h-4" />, sub: "Workforce — Workday, SAP" }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center space-x-5 p-6 bg-white border border-zodiac/5 rounded-[1.8rem] shadow-sm hover:shadow-xl hover:border-accent/20 transition-all duration-500 group/item">
                  <div className="w-10 h-10 rounded-xl bg-brandBg flex items-center justify-center text-zodiac/40 group-hover/item:text-accent transition-colors shadow-inner">
                    {item.i}
                  </div>
                  <div className="text-left">
                    <p className="text-[11px] font-black uppercase tracking-tight text-zodiac">{item.name}</p>
                    <p className="text-[9px] font-bold text-zodiac/30 uppercase tracking-widest">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
      
      <style>{`
        .group:hover .[transform-style\:preserve-3d] {
          transform: rotateY(180deg);
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 12s linear infinite;
        }
      `}</style>
    </section>
  );
};
